import LoadingSpinner from '@/components/common/LoadingSpinner'
import {
  LogTable,
  LogDetailModal,
  LEVEL_COLORS,
  LEVEL_LABELS,
} from '@/components/logs'
import { DEFAULT_PAGE_SIZE } from '@/config/constants'
import type { LogLevel } from '@/types/frontendLog'
import { useLogsPage } from '@/hooks/useLogsPage'

const LEVEL_OPTIONS: { value: LogLevel | ''; label: string }[] = [
  { value: '', label: 'すべて' },
  { value: 'error', label: 'ERROR' },
  { value: 'warn', label: 'WARN' },
  { value: 'info', label: 'INFO' },
  { value: 'debug', label: 'DEBUG' },
]

// 発生元アプリ (context.app で判別。webapp は context.app 無しのログ)
const APP_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'すべて' },
  { value: 'webapp', label: '操作アプリ (webapp)' },
  { value: 'polemap_v2', label: 'ポールマップ編集' },
]

export default function LogsPage() {
  const lp = useLogsPage()

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">ログ</h1>
          {lp.isFetching && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <LoadingSpinner size="sm" />
              <span>読み込み中...</span>
            </div>
          )}
        </div>
      </div>

      {/* レベル別サマリー */}
      {lp.summary && (
        <div className="flex gap-4">
          {Object.entries(lp.summary.level_counts).map(([level, count]) => (
            <div
              key={level}
              className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border"
              style={{
                borderLeftColor: LEVEL_COLORS[level as LogLevel],
                borderLeftWidth: 4,
              }}
            >
              <span
                className="text-sm font-medium"
                style={{ color: LEVEL_COLORS[level as LogLevel] }}
              >
                {LEVEL_LABELS[level as LogLevel]}
              </span>
              <span className="text-lg font-bold text-gray-900">{count}</span>
            </div>
          ))}
        </div>
      )}

      {/* フィルター */}
      <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">日付:</label>
          <input
            type="date"
            value={lp.selectedDate}
            onChange={(e) => lp.handleDateChange(e.target.value)}
            disabled={lp.isFetching}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">サイト:</label>
          <select
            value={lp.siteFilter}
            onChange={(e) => lp.handleSiteFilterChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
          >
            <option value="">すべて</option>
            {lp.siteIds.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">
            フォークID:
          </label>
          <select
            value={lp.forkIdFilter}
            onChange={(e) => lp.handleForkIdFilterChange(e.target.value)}
            disabled={lp.isFetching}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
          >
            <option value="">すべて</option>
            {lp.forkIds.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">レベル:</label>
          <select
            value={lp.levelFilter}
            onChange={(e) => lp.handleLevelFilterChange(e.target.value)}
            disabled={lp.isFetching}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
          >
            {LEVEL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">アプリ:</label>
          <select
            value={lp.appFilter}
            onChange={(e) => lp.handleAppFilterChange(e.target.value)}
            disabled={lp.isFetching}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
          >
            {APP_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <label className="text-sm font-medium text-gray-700">検索:</label>
          <input
            type="text"
            value={lp.searchInput}
            onChange={(e) => lp.handleSearchInputChange(e.target.value)}
            placeholder="メッセージ、アクションで検索..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
          />
        </div>
      </div>

      {/* エラー表示 */}
      {lp.error && (
        <div className="text-center py-8 text-red-600 bg-red-50 rounded-lg">
          データの取得に失敗しました
        </div>
      )}

      {/* ログテーブル */}
      <div
        className={`bg-white rounded-lg shadow overflow-hidden relative ${lp.isFetching ? 'opacity-60' : ''}`}
      >
        {lp.isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
            <div className="flex flex-col items-center gap-2">
              <LoadingSpinner size="lg" />
              <span className="text-gray-600">データを取得中...</span>
            </div>
          </div>
        )}

        <LogTable
          logs={lp.paginatedLogs}
          onDetailClick={lp.setSelectedLog}
          siteNameMap={{}}
          showRobotId={true}
          showCategory={true}
        />
      </div>

      {/* ページネーション */}
      {lp.totalFiltered > DEFAULT_PAGE_SIZE && (
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-1">
            <button
              onClick={() => lp.setPage((p) => Math.max(1, p - 1))}
              disabled={lp.page === 1}
              className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-sm"
            >
              前へ
            </button>
            {lp.pageNumbers.map((p, i) =>
              p === '...' ? (
                <span
                  key={`dots-${i}`}
                  className="px-2 py-2 text-sm text-gray-400"
                >
                  ...
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => lp.setPage(p)}
                  className={`px-3 py-2 border rounded-lg text-sm ${
                    p === lp.page
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {p}
                </button>
              )
            )}
            <button
              onClick={() => lp.setPage((p) => p + 1)}
              disabled={lp.page >= lp.totalPages}
              className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-sm"
            >
              次へ
            </button>
          </div>
          <p className="text-sm text-gray-600">
            {lp.totalFiltered} 件中 {(lp.page - 1) * DEFAULT_PAGE_SIZE + 1} -{' '}
            {Math.min(lp.page * DEFAULT_PAGE_SIZE, lp.totalFiltered)} 件を表示
          </p>
        </div>
      )}

      {/* ログ詳細モーダル */}
      {lp.selectedLog && (
        <LogDetailModal
          log={lp.selectedLog}
          onClose={() => lp.setSelectedLog(null)}
          logs={lp.paginatedLogs}
          onNavigate={lp.setSelectedLog}
        />
      )}
    </div>
  )
}
